import { allContractorSubscriptionPlanOptions, contractorStatusOptions } from "constants/SelectFieldOptions"
import SubscriptionPlanTitlesWithPrefix from "constants/SubscriptionPlanTitlesWithPrefix"
import { ApiTypes, UITypes } from "types"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { parseDateField } from "utils/dateUtils"
import { ContractorStatus } from "components/table/columnComponents/ContractorStatus"
import RowExpandToggle from "components/table/columnComponents/RowExpandToggle"

const getMetricsValue = (
  row: ApiTypes.Model.EnterpriseContractorListRow,
  contractor: ApiTypes.Model.Contractor | undefined,
  key: keyof ApiTypes.Model.EnterpriseContractorListMetrics,
) => (row.type === "enterprise" ? row.enterprise?.metrics?.[key] : contractor?.[key])

const getContractorTypeLabel = (row: ApiTypes.Model.EnterpriseContractorListRow) => {
  if (row.type === "enterprise") {
    return `Enterprise - ${row.enterprise?.contractor_count ?? row.contractors?.length ?? 0} linked`
  }

  return "Single"
}

const getRowSource = (row: ApiTypes.Model.EnterpriseContractorListRow) =>
  row.type === "enterprise" ? row.enterprise : row.contractors?.[0]

const getContractorCells = (
  row: ApiTypes.Model.EnterpriseContractorListRow,
  contractor?: ApiTypes.Model.Contractor,
): Omit<UITypes.TableBodyRow, "rowSettings"> => {
  const source = getRowSource(row)
  const subscriptionPlan = source?.subscription_plan || SubscriptionPlan.BASIC

  return {
    group_toggle: { value: "", hidden: row.type !== "enterprise" },
    name: { value: source?.name },
    brand: { value: source?.brand },
    type: { value: getContractorTypeLabel(row) },
    account_status_id: { value: source?.account_status ? String(source.account_status.id) : "" },
    is_enabled: { value: source?.is_enabled ? "1" : "0" },
    last_login: { value: parseDateField(source?.last_login || null) },
    create_date: { value: parseDateField(source?.created_at || null) },
    warranty: { value: getMetricsValue(row, contractor, "warranty") },
    country: { value: source?.country?.name },
    state: { value: source?.state?.name },
    purchased: { value: getMetricsValue(row, contractor, "purchased_devices") },
    installed: { value: getMetricsValue(row, contractor, "installed_devices") },
    inventory: { value: getMetricsValue(row, contractor, "inventory_devices") },
    install_7_day: { value: getMetricsValue(row, contractor, "install_7_day") },
    active_mobile: { value: getMetricsValue(row, contractor, "active_mobile") },
    subscription_plan: { value: SubscriptionPlanTitlesWithPrefix[subscriptionPlan] },
  }
}

const convertContractorToSubRow = (contractor: ApiTypes.Model.Contractor): UITypes.TableBodyRow =>
  ({
    group_toggle: { value: "", hidden: true },
    name: { value: contractor.name },
    brand: { value: contractor.brand },
    type: { value: "Single" },
    account_status_id: { value: contractor.account_status ? String(contractor.account_status.id) : "" },
    is_enabled: { value: contractor.is_enabled ? "1" : "0" },
    last_login: { value: parseDateField(contractor.last_login) },
    create_date: { value: parseDateField(contractor.created_at) },
    warranty: { value: contractor.warranty },
    country: { value: contractor.country?.name },
    state: { value: contractor.state?.name },
    purchased: { value: contractor.purchased_devices },
    installed: { value: contractor.installed_devices },
    inventory: { value: contractor.inventory_devices },
    install_7_day: { value: contractor.install_7_day },
    active_mobile: { value: contractor.active_mobile },
    subscription_plan: {
      value: SubscriptionPlanTitlesWithPrefix[contractor.subscription_plan || SubscriptionPlan.BASIC],
    },
    rowSettings: {
      id: Number(contractor.id),
      item: { type: "contractor", contractor },
      isEnable: !!contractor.is_enabled,
    },
  }) as UITypes.TableBodyRow

export const convertEnterpriseContractorsToTableRows = (
  data?: ApiTypes.Model.EnterpriseContractorListRow[],
): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(row => {
    const contractor = row.contractors?.[0]
    const source = getRowSource(row)
    const rowId = row.type === "enterprise" ? Number(row.enterprise?.id) : Number(contractor?.id)

    return {
      ...getContractorCells(row, contractor),
      subRows: row.type === "enterprise" ? row.contractors?.map(convertContractorToSubRow) || [] : [],
      rowSettings: {
        id: rowId,
        item: row,
        isEnable: !!source?.is_enabled,
      },
    } as UITypes.TableBodyRow
  })
}

export const getEnterpriseContractorTableHeaderCells = (
  contractorAccountStatusOptions: UITypes.Option[],
): UITypes.TableHeaderCell[] => [
  {
    title: "",
    name: "group_toggle",
    cellComponent: RowExpandToggle,
    size: "mini",
  },
  {
    title: "Company name",
    type: "sort-search",
    name: "name",
  },
  {
    title: "Brand Name",
    type: "sort-search",
    name: "brand",
  },
  {
    title: "Type",
    type: "select",
    name: "type",
    options: [
      { label: "Enterprise", value: "enterprise" },
      { label: "Single", value: "single" },
    ],
  },
  {
    title: "Contractor Status",
    type: "color-select",
    name: "account_status_id",
    options: contractorAccountStatusOptions,
    cellComponent: ContractorStatus,
  },
  {
    title: "Account Status",
    type: "select",
    name: "is_enabled",
    options: contractorStatusOptions,
    cellComponent: "boolean",
  },
  {
    title: "Last Login",
    type: "default",
    name: "last_login",
  },
  {
    title: "Creation Date",
    type: "sort-date-range",
    name: "create_date",
  },
  {
    title: "Warranty",
    type: "sort-search-number",
    name: "warranty",
  },
  {
    title: "Country",
    type: "sort-search",
    name: "country",
  },
  {
    title: "State",
    type: "sort-search",
    name: "state",
  },
  {
    title: "Subscription Plan",
    type: "select",
    name: "subscription_plan",
    options: allContractorSubscriptionPlanOptions,
  },
  {
    title: "Purchased Devices",
    type: "sort-search-number",
    name: "purchased",
  },
  {
    title: "Installed Thermostats",
    type: "sort-search-number",
    name: "installed",
  },
  {
    title: "Installed Last 7 days",
    type: "sort",
    name: "install_7_day",
  },
  {
    title: "Activated mobile apps",
    type: "sort",
    name: "active_mobile",
  },
  {
    title: "Inventory",
    type: "sort-search-number",
    name: "inventory",
  },
]
